import { useEffect, useState } from 'react'

import { api, can, refusalReason, type ApiError, type Capability, type ScoringModel } from '../lib/api'
import { Icon } from './ui'
import { Status, Toggletip } from './kit'

type Template = {
  code: string
  name: string
  description: string
  questions: number
  sections: number
  file: string
  // Each questionnaire ships with its own model, because the engine refuses
  // a model that weighs a key the questionnaire does not carry.
  riskModel: { code: string; file: string; weights: number; gates: number }
}
type Index = { generatedOn: string; templates: Template[] }

// The standard questionnaires ship with the product as files under
// /standard-questionnaires and land in a tenant through the same import path
// as a hand-written document. Nothing here is a hidden seed: what a customer
// gets is exactly what they would get by importing the file themselves, which
// is also how they add their own.
export function StandardTemplates({ existing, onAdded }: { existing: Set<string>; onAdded: () => void }) {
  const [index, setIndex] = useState<Index | null>(null)
  const [models, setModels] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<string | null>(null)
  // The outcome as one line, with what happens next behind the (i) when there
  // is a next step to name.
  const [note, setNote] = useState<{ text: string; tone: 'ok' | 'warn'; tip?: string } | null>(null)

  useEffect(() => {
    fetch('/standard-questionnaires/index.json')
      .then((r) => (r.ok ? (r.json() as Promise<Index>) : null))
      .then(setIndex)
      .catch(() => setIndex(null))
    api
      .riskModels()
      .then((r) => setModels(new Set(r.riskModels.map((m) => m.code.toUpperCase()))))
      .catch(() => setModels(new Set()))
  }, [])

  if (!index || index.templates.length === 0) return null

  async function add(t: Template) {
    if (!index) return
    setBusy(t.code)
    setNote(null)
    try {
      const doc = await fetch('/standard-questionnaires/' + t.file).then((r) => r.json())
      const r = await api.importQuestionnaireDoc(doc)
      let modelNote = ''
      if (!models.has(t.riskModel.code.toUpperCase())) {
        const m = (await fetch('/standard-questionnaires/' + t.riskModel.file).then((r) => r.json())) as {
          code: string
          name: string
          description: string
          notes: string
          model: ScoringModel
        }
        const created = await api.createRiskModel({ code: m.code, name: m.name, description: m.description })
        await api.createRiskModelVersion(created.id, { notes: m.notes, model: m.model })
        setModels((prev) => new Set([...prev, m.code.toUpperCase()]))
        modelNote = ', its risk model ' + m.name + ' with it'
      }
      setNote({
        text: t.name + ' added as draft v' + r.versionNo + ', ' + r.questionCount + ' questions' + modelNote,
        tone: 'ok',
        tip: 'A second person publishes the questionnaire naming its model, then promotes the model against the published version.',
      })
      onAdded()
    } catch (e) {
      const err = e as ApiError
      setNote({ text: err?.message ?? String(e), tone: 'warn' })
    } finally {
      setBusy(null)
    }
  }

  // Adding a template writes the questionnaire (questionnaire.author) and, when
  // the tenant does not have the model that scores it yet, the risk model too
  // (riskmodel.author, POST /v1/risk-models). The button asked only for the
  // first until release 3.1, so somebody who could not write models pressed it
  // and got half a template and a 403 (persona spec 6.3, T2).
  const refusedFor = (t: Template): Capability | null => {
    if (!can('questionnaire.author')) return 'questionnaire.author'
    if (!models.has(t.riskModel.code.toUpperCase()) && !can('riskmodel.author')) return 'riskmodel.author'
    return null
  }
  return (
    <section className="panel std" aria-labelledby="std-heading">
      <div className="std-head">
        <div>
          <h2 id="std-heading">Start from a standard</h2>
        </div>
      </div>
      {note && (
        <p className={'note ' + (note.tone === 'warn' ? 'warn' : 'ok')} role="status">
          {note.text}
          {note.tip && (
            <>
              {' '}
              <Toggletip label="What happens next">{note.tip}</Toggletip>
            </>
          )}
        </p>
      )}
      <div className="std-grid">
        {index.templates.map((t) => {
          const here = existing.has(t.code.toUpperCase())
          return (
            <article className="std-card" key={t.code}>
              <div className="std-icon">
                <Icon name="file" size={20} />
              </div>
              <h3>{t.name}</h3>
              <p>{t.description}</p>
              <div className="std-meta">
                <span className="chip">{t.questions} questions</span>
                <span className="chip">{t.sections} sections</span>
                <span className="chip">Yes or no</span>
              </div>
              {here ? (
                <Status tone="plain" title="Already in this tenant">
                  Already added
                </Status>
              ) : refusedFor(t) !== null ? (
                <Status tone="plain" title={refusalReason(refusedFor(t) as Capability)}>
                  Not yours to add
                </Status>
              ) : (
                <button
                  type="button"
                  className="button primary"
                  disabled={busy !== null}
                  data-action="tprm.questionnaires.a01"
                  data-action-also="tprm.questionnaires.a02 tprm.questionnaires.a03"
                  onClick={() => void add(t)}
                >
                  {busy === t.code ? 'Adding' : 'Add to this tenant'}
                </button>
              )}
            </article>
          )
        })}
      </div>
    </section>
  )
}
